import type { SessionKickoffPayload } from '@frontend/api-types';
import { SECURE_SESSION_SOCKET_EVENTS } from '@frontend/constants';
import type { SocketClient } from '@frontend/sockets';
import { useCallback, useEffect, useRef, useState } from 'react';

export type UseSecureSessionOptions = {
  authToken: string;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
  secureNoteId: string;
  taskId?: string;
};

export type UseSecureSessionReturn = {
  abandonSession: () => void;
  liveViewUrl: string | null;
};

export const useSecureSession = (
  options: UseSecureSessionOptions,
  socketClient: SocketClient,
): UseSecureSessionReturn => {
  const { authToken, onError, onSuccess, secureNoteId, taskId } = options;
  const [liveViewUrl, setLiveViewUrl] = useState<string | null>(null);
  const sessionEndedRef = useRef(false);
  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  // eslint-disable-next-line fp/no-mutation
  onErrorRef.current = onError;
  // eslint-disable-next-line fp/no-mutation
  onSuccessRef.current = onSuccess;

  const log = (direction: 'rx' | 'tx', event: string) =>
    // eslint-disable-next-line no-console
    console.log(`[SecureSession] ${direction === 'tx' ? '>>>' : '<<<'} ${event}`);

  const abandonSession = useCallback(() => {
    const socket = socketClient.getSocket('secureSession');
    if (socket?.connected) {
      // eslint-disable-next-line fp/no-mutation
      sessionEndedRef.current = true;
      log('tx', SECURE_SESSION_SOCKET_EVENTS.ABANDONED_BY_CLIENT);
      socket.emit(SECURE_SESSION_SOCKET_EVENTS.ABANDONED_BY_CLIENT);
    }
  }, [socketClient]);

  useEffect(() => {
    // eslint-disable-next-line fp/no-mutation
    sessionEndedRef.current = false;

    const existingSocket = socketClient.getSocket('secureSession');
    if (existingSocket?.connected || existingSocket?.active) {
      socketClient.disconnectSocket();
    }

    const socket = socketClient.initializeSocket<'secureSession'>({
      channelType: 'secureSession',
      token: authToken,
    });

    const handleConnect = () => {
      log('rx', 'connect');
      const payload = {
        secure_note: secureNoteId,
        ...(taskId && { task_id: taskId }),
      };

      try {
        log('tx', `${SECURE_SESSION_SOCKET_EVENTS.KICKOFF} secureNoteId=${secureNoteId}`);
        socket.emit<typeof SECURE_SESSION_SOCKET_EVENTS.KICKOFF>(
          SECURE_SESSION_SOCKET_EVENTS.KICKOFF,
          payload as unknown as SerializedJson<SessionKickoffPayload>,
        );
      } catch (error) {
        onErrorRef.current?.(new Error('Failed to emit kickoff event'));
      }
    };

    const handleSessionLaunched = (data: unknown) => {
      log('rx', SECURE_SESSION_SOCKET_EVENTS.LAUNCHED);
      const parsedData = JSON.parse(data as string);

      const liveViewUrl = parsedData.live_view_url;
      if (liveViewUrl) {
        setLiveViewUrl(liveViewUrl);
      } else {
        onErrorRef.current?.(new Error('No live view URL found'));
      }
    };

    const markSessionEnded = () => {
      // eslint-disable-next-line fp/no-mutation
      sessionEndedRef.current = true;
    };

    const handleSessionSucceeded = () => {
      log('rx', SECURE_SESSION_SOCKET_EVENTS.SUCCEEDED);
      markSessionEnded();
      onSuccessRef.current?.();
    };

    const handleSessionFailed = () => {
      log('rx', SECURE_SESSION_SOCKET_EVENTS.FAILED);
      markSessionEnded();
      onErrorRef.current?.(new Error('Session failed'));
    };

    const handleDisconnect = () => {
      log('rx', 'disconnect');
      if (!sessionEndedRef.current) {
        onErrorRef.current?.(new Error('Disconnected from secure session'));
      }
    };

    const handleConnectError = () => {
      log('rx', 'connect_error');
      markSessionEnded();
      onErrorRef.current?.(new Error('Failed to connect to secure session'));
    };

    const handleTimeout = () => {
      log('rx', 'session_timed_out');
      markSessionEnded();
      onErrorRef.current?.(new Error('Timeout while connecting to secure session'));
    };

    const handleTryLater = () => {
      log('rx', SECURE_SESSION_SOCKET_EVENTS.TRY_LATER);
      markSessionEnded();
      onErrorRef.current?.(new Error('Sorry! Please try again in a minute.'));
    };

    const handleEventHandlerError = () => {
      log('rx', SECURE_SESSION_SOCKET_EVENTS.EVENT_HANDLER_ERROR);
      markSessionEnded();
      onErrorRef.current?.(new Error('Event handler error'));
    };
    const handleError = () => {
      log('rx', 'error');
      markSessionEnded();
      onErrorRef.current?.(new Error('Error while connecting to secure session'));
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleConnectError);
    socket.on(SECURE_SESSION_SOCKET_EVENTS.LAUNCHED, handleSessionLaunched);
    socket.on(SECURE_SESSION_SOCKET_EVENTS.SUCCEEDED, handleSessionSucceeded);
    socket.on(SECURE_SESSION_SOCKET_EVENTS.FAILED, handleSessionFailed);
    socket.on(SECURE_SESSION_SOCKET_EVENTS.TIMED_OUT_NO_ACTIVITY, handleTimeout);
    socket.on(SECURE_SESSION_SOCKET_EVENTS.TIMED_OUT_UNABLE_TO_LOGIN, handleTimeout);
    socket.on(SECURE_SESSION_SOCKET_EVENTS.TRY_LATER, handleTryLater);
    socket.on(SECURE_SESSION_SOCKET_EVENTS.EVENT_HANDLER_ERROR, handleEventHandlerError);

    socket.io.on('error', handleError);

    if (socket.connected) {
      handleConnect();
    }

    return () => {
      if (!sessionEndedRef.current && socket.connected) {
        log('tx', SECURE_SESSION_SOCKET_EVENTS.ABANDONED_BY_CLIENT);
        socket.emit(SECURE_SESSION_SOCKET_EVENTS.ABANDONED_BY_CLIENT);
      }
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off(SECURE_SESSION_SOCKET_EVENTS.LAUNCHED, handleSessionLaunched);
      socket.off(SECURE_SESSION_SOCKET_EVENTS.SUCCEEDED, handleSessionSucceeded);
      socket.off(SECURE_SESSION_SOCKET_EVENTS.FAILED, handleSessionFailed);
      socket.off(SECURE_SESSION_SOCKET_EVENTS.TIMED_OUT_NO_ACTIVITY, handleTimeout);
      socket.off(SECURE_SESSION_SOCKET_EVENTS.TIMED_OUT_UNABLE_TO_LOGIN, handleTimeout);
      socket.off(SECURE_SESSION_SOCKET_EVENTS.TRY_LATER, handleTryLater);
      socket.off(SECURE_SESSION_SOCKET_EVENTS.EVENT_HANDLER_ERROR, handleEventHandlerError);
      socket.io.off('error', handleError);
      socket.removeAllListeners();
      socketClient.disconnectSocket();
    };
  }, [secureNoteId, authToken, socketClient, taskId]);

  return {
    abandonSession,
    liveViewUrl,
  };
};
